import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Dropdown, Menu, Avatar, Typography } from 'antd';
import { Bot, MessageSquare, X, Send, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import '../lite.css'; // Add some custom styling

const { Text } = Typography;

const AIChatBot = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([
        { id: 1, text: "Hey there! I'm your PushCodex AI Assistant. How can I help you set up your display loop today?", sender: 'ai' }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (isOpen) {
            scrollToBottom();
        }
    }, [messages, isOpen, isTyping]);

    const handleSend = () => {
        if (!inputValue.trim()) return;

        const userMsg = { id: Date.now(), text: inputValue, sender: 'user' };
        setMessages((prev) => [...prev, userMsg]);
        setInputValue('');
        setIsTyping(true);

        // Mock AI Response
        setTimeout(() => {
            const aiMsg = {
                id: Date.now() + 1,
                text: getMockResponse(userMsg.text),
                sender: 'ai'
            };
            setMessages((prev) => [...prev, aiMsg]);
            setIsTyping(false);
        }, 1500);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleSend();
        }
    };

    const getMockResponse = (input) => {
        const lowerInput = input.toLowerCase();
        if (lowerInput.includes('video') || lowerInput.includes('upload')) {
            return "You can easily upload videos and images by navigating to the 'Looping Content' tab. Just drag and drop your media there!";
        }
        if (lowerInput.includes('screen') || lowerInput.includes('pair')) {
            return "To pair a new screen, go to the 'My Display' tab, click 'Pair a Screen', and enter the 6-digit code shown on your PushCodex TV app.";
        }
        if (lowerInput.includes('hi') || lowerInput.includes('hello')) {
            return "Hello! I'm here to help. Is there anything specific you need assistance with in the Lite Workspace?";
        }
        return "That's a great question! While I'm just a mockup right now, in the future I'll be able to help you automatically schedule content, suggest layouts, and troubleshoot offline screens!";
    };

    return (
        <div style={{ position: 'fixed', bottom: '24px', right: '24px', zIndex: 9999 }}>
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 50, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 50, scale: 0.9 }}
                        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        style={{
                            position: 'absolute',
                            bottom: '80px',
                            right: '0',
                            width: '350px',
                            height: '500px',
                            background: '#ffffff',
                            borderRadius: '16px',
                            boxShadow: '0 10px 40px -10px rgba(0,0,0,0.2)',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            border: '1px solid #f3f4f6'
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                            padding: '16px 20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            color: '#ffffff'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <Avatar size={40} style={{ background: '#ffffff', color: '#1890ff' }} icon={<Bot size={24} />} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: '16px' }}>PushCodex AI</div>
                                    <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                                        <Sparkles size={12} style={{ marginRight: '4px', display: 'inline' }} />
                                        Smart Assistant
                                    </div>
                                </div>
                            </div>
                            <Button
                                type="text"
                                icon={<X size={20} color="#ffffff" />}
                                onClick={() => setIsOpen(false)}
                                style={{ padding: 0, background: 'transparent' }}
                            />
                        </div>

                        {/* Chat Context */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f9fafb', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                            {messages.map((msg) => (
                                <motion.div
                                    key={msg.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{
                                        display: 'flex',
                                        flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
                                        gap: '12px',
                                        alignItems: 'flex-end'
                                    }}
                                >
                                    {msg.sender === 'ai' && (
                                        <Avatar size={32} style={{ background: '#1890ff', flexShrink: 0 }} icon={<Bot size={18} />} />
                                    )}
                                    <div style={{
                                        background: msg.sender === 'user' ? '#1890ff' : '#ffffff',
                                        color: msg.sender === 'user' ? '#ffffff' : '#111827',
                                        padding: '12px 16px',
                                        borderRadius: '16px',
                                        borderBottomRightRadius: msg.sender === 'user' ? '4px' : '16px',
                                        borderBottomLeftRadius: msg.sender === 'ai' ? '4px' : '16px',
                                        maxWidth: '75%',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        border: msg.sender === 'ai' ? '1px solid #e5e7eb' : 'none',
                                        fontSize: '14px',
                                        lineHeight: '1.5'
                                    }}>
                                        {msg.text}
                                    </div>
                                </motion.div>
                            ))}
                            {isTyping && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    style={{ display: 'flex', flexDirection: 'row', gap: '12px', alignItems: 'flex-end' }}
                                >
                                    <Avatar size={32} style={{ background: '#1890ff', flexShrink: 0 }} icon={<Bot size={18} />} />
                                    <div style={{
                                        background: '#ffffff',
                                        padding: '16px',
                                        borderRadius: '16px',
                                        borderBottomLeftRadius: '4px',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                        border: '1px solid #e5e7eb',
                                    }}>
                                        <div className="typing-indicator">
                                            <span></span><span></span><span></span>
                                        </div>
                                    </div>
                                </motion.div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        {/* Input Area */}
                        <div style={{ padding: '16px', background: '#ffffff', borderTop: '1px solid #f3f4f6', display: 'flex', gap: '12px' }}>
                            <Input
                                placeholder="Ask me anything..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                style={{ borderRadius: '24px', padding: '8px 16px' }}
                            />
                            <Button
                                type="primary"
                                shape="circle"
                                size="large"
                                icon={<Send size={18} />}
                                onClick={handleSend}
                                disabled={!inputValue.trim()}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
            >
                <Button
                    type="primary"
                    shape="circle"
                    size="large"
                    onClick={() => setIsOpen(!isOpen)}
                    style={{
                        width: '64px',
                        height: '64px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 8px 16px rgba(24, 144, 255, 0.4)',
                        background: isOpen ? '#ff4d4f' : 'linear-gradient(135deg, #1890ff 0%, #0050b3 100%)',
                        border: 'none',
                    }}
                >
                    {isOpen ? <X size={28} color="#ffffff" /> : <MessageSquare size={28} color="#ffffff" />}
                </Button>
            </motion.div>
        </div>
    );
};

export default AIChatBot;
